import { Copy, ExternalLink } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'

const CalendarShareButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: ${theme.transitions.default};
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const CalendarShareDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${theme.spacing.xs};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  box-shadow: ${({ theme }) => theme.shadows.md};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transform: ${(props) => (props.$isOpen ? 'scale(1)' : 'scale(0.95)')};
  transition: ${theme.transitions.default};
  z-index: 1000;
  min-width: 300px;
  padding: ${theme.spacing.md};
`

const ShareTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`

const ShareDescription = styled.div`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.md};
  line-height: 1.4;
`

const ShareActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textInverted || theme.colors.background};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const CopyUrlContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  align-items: center;
`

const UrlInput = styled.input`
  flex: 1;
  padding: ${theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.xs};
  font-family: monospace;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

export interface CalendarShareButtonProps {
  isOpen: boolean
  calendarUrl?: string
  isLoadingCalendarUrl?: boolean
  onToggleDropdown: (e: React.MouseEvent) => void
  onGenerateCalendarUrl: () => Promise<void>
  onCopyCalendarUrl: () => Promise<void>
}

export const CalendarShareButton: React.FC<CalendarShareButtonProps> = ({
  isOpen,
  calendarUrl,
  isLoadingCalendarUrl = false,
  onToggleDropdown,
  onGenerateCalendarUrl,
  onCopyCalendarUrl,
}) => {
  return (
    <CalendarShareButtonContainer onClick={onToggleDropdown} title="Share calendar">
      <ExternalLink size={16} />
      <CalendarShareDropdown $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <ShareTitle>
          <ExternalLink size={16} />
          Share Calendar
        </ShareTitle>
        <ShareDescription>
          Generate a secure CalDAV URL to subscribe to your workout calendar in other apps like Apple Calendar, Google Calendar, or Outlook.
        </ShareDescription>
        <ShareActions>
          {!calendarUrl ? (
            <ShareButton onClick={onGenerateCalendarUrl} disabled={isLoadingCalendarUrl}>
              {isLoadingCalendarUrl ? 'Generating...' : 'Generate Share URL'}
            </ShareButton>
          ) : (
            <CopyUrlContainer>
              <UrlInput value={calendarUrl} readOnly />
              <CopyButton onClick={onCopyCalendarUrl} title="Copy URL">
                <Copy size={14} />
              </CopyButton>
            </CopyUrlContainer>
          )}
        </ShareActions>
      </CalendarShareDropdown>
    </CalendarShareButtonContainer>
  )
}